import { ErrorInfo } from 'react';
import { errorsApi } from '@/lib/api';

interface ErrorReport {
  message: string;
  stack?: string;
  componentStack?: string;
  url: string;
  userAgent: string;
  timestamp: number;
  level: 'root' | 'page' | 'section' | 'component';
  userId?: string;
  metadata?: Record<string, any>;
}

class ErrorReportingService {
  private reportQueue: ErrorReport[] = [];
  private isReporting = false;
  private maxQueueSize = 10;
  private reportDelay = 2000; // 2 seconds debounce

  /**
   * Report an error to the backend
   */
  async reportError(
    error: Error,
    errorInfo: ErrorInfo,
    level: 'root' | 'page' | 'section' | 'component' = 'component',
    metadata?: Record<string, any>
  ): Promise<void> {
    try {
      const report: ErrorReport = {
        message: error.message,
        stack: error.stack,
        componentStack: errorInfo.componentStack,
        url: window.location.href,
        userAgent: navigator.userAgent,
        timestamp: Date.now(),
        level,
        metadata,
      };

      // Add to queue
      this.reportQueue.push(report);

      // Limit queue size
      if (this.reportQueue.length > this.maxQueueSize) {
        this.reportQueue.shift();
      }

      // Debounce reporting
      if (!this.isReporting) {
        this.isReporting = true;
        setTimeout(() => this.flushQueue(), this.reportDelay);
      }
    } catch (err) {
      // Fail silently - don't let error reporting cause more errors
      console.error('Failed to report error:', err);
    }
  }

  /**
   * Flush the error queue to the backend
   */
  private async flushQueue(): Promise<void> {
    if (this.reportQueue.length === 0) {
      this.isReporting = false;
      return;
    }

    const reports = [...this.reportQueue];
    this.reportQueue = [];

    try {
      // Send errors to backend using errorsApi helper
      await errorsApi.report(reports);
    } catch (err) {
      // Fail silently
      console.error('Failed to send error reports:', err);
    } finally {
      this.isReporting = false;
    }
  }
}

// Export singleton instance
export const errorReportingService = new ErrorReportingService();
